/** The summary is what you read when you were not there: the first two paragraphs, no headings. */
/**
 * The two paragraphs somebody reads when they were not there.
 *
 * ⚠ A heading and the sentence under it are **one block** when the model
 * writes them on consecutive lines, which is ordinary markdown and what it
 * usually does. Dropping every block that starts with `#` therefore threw away
 * the prose with the heading — which is why Vladimir's daily had a note of
 * nearly three thousand characters and an empty Summary tab (2026-09-21). The
 * heading lines come off each block; what is left is the prose.
 */
export function summaryOf(markdown: string | null): string[] {
  if (!markdown) return [];
  return markdown
    .split(/\n\s*\n/)
    .map((block) => {
      const lines = block.split('\n');
      // Leading heading lines only: a `#` inside a paragraph is a character
      // somebody typed, not a heading.
      let i = 0;
      while (i < lines.length && /^\s*#{1,6}\s/.test(lines[i])) i += 1;
      return lines.slice(i).join('\n').trim();
    })
    .filter((p) => p && !/^[-*]\s/.test(p) && !/^\d+\.\s/.test(p) && !/^>/.test(p))
    .slice(0, 2);
}

export function copyText(text: string): Promise<void> {
  return navigator.clipboard.writeText(text);
}

/** The note's own heading, when it has one. */
export function titleOf(markdown: string | null): string | null {
  const m = markdown?.match(/^#{1,3}\s+(.+)$/m);
  if (!m) return null;
  const t = m[1].replace(/^[^—:]*—\s*/, '').trim();
  return t ? t[0].toUpperCase() + t.slice(1) : null;
}

export const sentenceCount = (paragraphs: string[]): number =>
  paragraphs
    .join(' ')
    .split(/[.!?](\s|$)/)
    .filter((s) => s.trim().length > 1).length;

/** "priya.raman@…" → "Priya Raman" when nothing better is known. */
export function guessName(email: string): string {
  const local = email.split('@')[0] ?? email;
  return local
    .split(/[._-]+/)
    .filter(Boolean)
    .map((p) => p[0].toUpperCase() + p.slice(1))
    .join(' ');
}

export const firstName = (name: string | null, email: string): string =>
  (name ?? guessName(email)).split(' ')[0];

/**
 * Does this person answer to the name the meeting heard?
 *
 * ⚠ A helper, never a decision. The server matches names to addresses when it
 * can (`owner-address.ts`); this is only for putting the likely person first
 * in a list somebody is about to choose from — so it is deliberately loose
 * where the server is strict, and never assigns anything on its own.
 */
export function answersTo(heard: string | null | undefined, name: string | null, email: string) {
  const wanted = (heard ?? '').trim().toLowerCase();
  if (!wanted) return false;
  const known = (name ?? guessName(email)).toLowerCase();
  return known.startsWith(wanted) || wanted.startsWith(known.split(' ')[0]);
}
